import { MathUtils } from '@3d-scene/common/util/math-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { BaseDimensionElementCreator } from '@3d-scene/layer/creator/base-dimension-element-creator';
import { DimensionElement } from '@3d-scene/layer/elements/dimension-element';
import { ILayerElement } from '@3d-scene/layer/elements/i-layer-element';
import { DimensionDescriptor } from '@3d-scene/layer/util/dimension-descriptor';
import { DimensionUtils } from '@3d-scene/layer/util/dimension.utils';
import { Line } from '@3d-scene/model/line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { IsProjectType } from '@project/model/is-project-type';
import { ProjectModel } from '@project/model/project.model';
import { Log } from '@shared/debug/log';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { Group, LineSegments, Mesh, MeshBasicMaterial, Object3D, SphereGeometry, Vector3 } from 'three';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { MountingRailCarrierType, MountingRailCreationModel } from '@configuration/wizard/service/mounting-rail-creation.model';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { ProjectDimensionsService } from '@project/components/project/service/project-dimensions.service';
import { SimpleTextValueVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ConfiguratorParameters } from '../../configurator-parameters/configurator-parameters';
import { SceneManager } from '../../manager/scene.manager';
import { ContainerService } from '../../model3d/container.service';
import { ConnectionInfoUtils } from '../../model3d/util/connection-info-utils';
import { GlobalArticleContainerUtils } from '../../model3d/util/global-article-container-utils';
import { DrillingHoleDimensionElement } from '../elements/drilling-hole-dimension-element';
import { LabelElement } from '../elements/label-element';
import { PointerLabelElement } from '../elements/pointer-label-element';

export class DimensionLayerElementCreator extends BaseDimensionElementCreator {
  protected static readonly FONT_SCALE_COEF: number = 0.173;
  protected static readonly FONT_SCALE_BASE_DISTANCE: number = 359;
  protected static readonly BUSBAR_OR_COVER_Y_OFFSET: number = 2;
  public static readonly LENGTH_LABEL_NAME = 'lengthLabel';
  public static readonly WIDTH_LABEL_NAME = 'widthLabel';
  public static readonly HEIGHT_LABEL_NAME = 'heightLabel';

  private readonly railLineDistanceToRail: number = 80;
  private readonly projectLineDistanceToRail: number = 150;

  protected material: MeshBasicMaterial = new MeshBasicMaterial({ color: 0x000000 });

  constructor(
    projectModel: ProjectModel,
    containerService: ContainerService,
    localeResourceManager: LocaleResourceManager,
    sceneManager: SceneManager,
    private projectDimensionsService: ProjectDimensionsService,
    private mountingRailHelper: MountingRailHelper,
    articleContainerUtils: ArticleContainerUtils,
    private articleUsageService: ArticleUsageService,
    private projectService: ProjectService,
    private globalArticleContainerUtils: GlobalArticleContainerUtils,
  ) {
    super(projectModel, localeResourceManager, sceneManager, containerService, articleContainerUtils, 'dimensions');
  }

  public createLayerElements(): Group {
    if (!this.projectModel || !this.mountingRailHelper) {
      return this.createLayerGroup();
    }

    const dimensionElements: ILayerElement[] = [];
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const maxZ: number = this.findMaxZValue();

    const allArticles: ArticleContainer[] = insertHelper.getAllConnectedArticles();
    const leadingArticlesPoints: number[] = this.collectLeadingArticlesPoints(allArticles, insertHelper);
    const busbarOrCoverArticles: ArticleContainer[] = this.getBusbarOrCoverArticles();

    const connectedArticles = allArticles.filter((container) => this.isIncludedInCalculation(container));

    let startPoint: Vector3;
    let endPoint: Vector3;

    if (IsProjectType.visibleMountingRail(this.projectModel.currentProject)) {
      startPoint = new Vector3(insertHelper.startPoint.x, insertHelper.startPoint.y, maxZ);
      endPoint = new Vector3(insertHelper.endPoint.x, insertHelper.endPoint.y, maxZ);
    } else if (IsProjectType.mcsGds(this.projectModel.currentProject)) {
      const allArticlesIncludedInCalculation: ArticleContainer[] = this.containerService
        .enumerateArticleContainersInGlobal()
        .filter((articleContainer) => this.isIncludedInCalculation(articleContainer));
      const startArticleGlobalPosition: Vector3 = this.calculateMinGlobalPointOfArticles(allArticlesIncludedInCalculation);
      const endArticleGlobalPosition: Vector3 = this.calculateMaxGlobalPointOfArticles(allArticlesIncludedInCalculation);
      startPoint = new Vector3(startArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
      endPoint = new Vector3(endArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
    } else {
      if (ArrayUtils.isEmpty(connectedArticles)) {
        return this.createLayerGroup();
      }
      const startArticleGlobalPosition: Vector3 = this.calculateMinGlobalArticlePoint(connectedArticles[0]);
      const endArticleGlobalPosition: Vector3 = this.calculateMaxGlobalArticlePoint(connectedArticles[connectedArticles.length - 1]);
      startPoint = new Vector3(startArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
      endPoint = new Vector3(endArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
    }

    const dimensionDescriptors: DimensionDescriptor[] = this.collectDimensionDescriptors(
      startPoint,
      endPoint,
      connectedArticles,
      leadingArticlesPoints
    );
    const busbarOrCoverDescriptors: DimensionDescriptor[] = this.createDimensionDescriptorsForArticles(busbarOrCoverArticles);
    this.processDimensionDescriptors(busbarOrCoverDescriptors, dimensionElements, true);

    let coversChildArticleIds: string[] = [];
    for (const busbarOrCoverArticle of busbarOrCoverArticles) {
      if (ArticleUtils.isCoverArticle(busbarOrCoverArticle.article)) {
        coversChildArticleIds = coversChildArticleIds.concat(ArticleUsageUtils.getAcceptingAreaIds(busbarOrCoverArticle.articleUsage));
      }
    }
    const coverChildContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(coversChildArticleIds);
    const coverChildDescriptors: DimensionDescriptor[] = this.createDimensionDescriptorsForArticles(coverChildContainers, true);
    this.processDimensionDescriptors(coverChildDescriptors, dimensionElements, true);

    const maxSizeLevel: number = this.processDimensionDescriptors(dimensionDescriptors, dimensionElements);

    // create main/base dimension element
    // if there is only one dimension for articles shown, then this already covers the entire mounting rail
    // and the main/base dimension is not needed
    if (dimensionDescriptors.length !== 1 && startPoint && endPoint) {
      const commonDimension: DimensionElement = new DimensionElement(
        this.sceneManager,
        [
          startPoint,
          startPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
          endPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
          endPoint,
        ],
        this.createTextValue(this.formatDimension(startPoint.distanceTo(endPoint) / 10)),
        this.blackMaterial,
        maxSizeLevel + 1
      );

      dimensionElements.push(commonDimension);
    }

    const projectDimensionsInMM = this.globalArticleContainerUtils.calculateProjectDimensionsInMM();

    const lengthLabel: string = this.localeResourceManager.getValueByKey('layerManager.dimension.projectLength', [
      this.getFormattedDimensionString(projectDimensionsInMM.width),
    ]) + ' ' + this.localeResourceManager.getValueByKey('global.unit.label.mm');
    const widthLabel: string = this.localeResourceManager.getValueByKey('layerManager.dimension.projectWidth', [
      this.getFormattedDimensionString(projectDimensionsInMM.depth),
    ]) + ' ' + this.localeResourceManager.getValueByKey('global.unit.label.mm');
    const heightLabel: string = this.localeResourceManager.getValueByKey('layerManager.dimension.projectHeight', [
      this.getFormattedDimensionString(projectDimensionsInMM.height),
    ]) + ' ' + this.localeResourceManager.getValueByKey('global.unit.label.mm');

    dimensionElements.push(this.createProjectLengthLabel(lengthLabel));
    dimensionElements.push(this.createProjectWidthLabel(widthLabel));
    dimensionElements.push(this.createProjectHeightLabel(heightLabel));

    if (IsProjectType.visibleMountingRail(this.projectModel.currentProject)) {
      this.createRailDimensionLineElement(dimensionElements);
    }
    this.createProjectDimensionLineElement(dimensionElements);

    if (this.projectService.isLockingClipProject(this.projectModel.currentProject)) {
      this.createLockingClipLineElement(dimensionElements);
    }

    return this.createLayerGroup(dimensionElements);
  }

  public createProjectLengthLabel(text: string): LabelElement {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    const labelLength: number = DimensionUtils.getTextWidth(textValue, LabelElement.DEFAULT_FONT_SIZE);
    const lengthLabel: LabelElement = new LabelElement(
      textValue,
      LabelElement.DEFAULT_FONT_SIZE,
      undefined,
      labelLength
    );
    lengthLabel.name = DimensionLayerElementCreator.LENGTH_LABEL_NAME;
    lengthLabel.position.set(this.getDimensionLabelXPosition(), 0, 100);
    return lengthLabel;
  }

  private createProjectDimensionLineElement(dimensionElements: any[]): void {
    const ProjectBoundBox = this.projectDimensionsService.getProjectBoundsBox(this.projectModel.currentProject);

    const start: Vector3 = new Vector3(this.projectLineDistanceToRail, 0, ProjectBoundBox.max.z);
    const end: Vector3 = new Vector3(this.projectLineDistanceToRail, 0, ProjectBoundBox.min.z);
    const lineTo: LineSegments = DimensionUtils.createLineSegment(start, end, DimensionElement.LINE_COLOR);
    dimensionElements.push(lineTo);

    const projectSize: number = UnitConverter.fromToRounded('3d', 'mm', ProjectBoundBox.getSize(new Vector3()).z, 1);
    dimensionElements.push(this.createVerticalLineDimensionLabel(this.localeResourceManager.formatNumber(projectSize) + ' mm', start.x));
    dimensionElements.push(...this.createVerticalArrow(start, end));
  }

  private createLockingClipLineElement(dimensionElements: any[]): void {
    let lockingClipConnections: Connection[] = this.getLockingClipConnections();
    const mountingRailContainer: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();
    const isFreeMounting: boolean = ArticleUtils.isFreeMounting(mountingRailContainer.article);
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    let startX = insertHelper.startPoint.x;
    let endX = insertHelper.endPoint.x;
    if (isFreeMounting) {
      const connectedArticles: ArticleContainer[] = insertHelper.getAllConnectedArticles();
      startX = this.calculateMinGlobalArticlePoint(connectedArticles[0]).x;
      endX = this.calculateMaxGlobalArticlePoint(connectedArticles[connectedArticles.length - 1]).x;
    }

    let lockingClipConnectionContainers: ArticleContainer[] = [];
    lockingClipConnections.forEach((connection) => {
      lockingClipConnectionContainers.push(connection.articleContainer);
    });

    const positionZ: number = isFreeMounting ? -500 : this.calculateMaxGlobalArticlePoint(mountingRailContainer).z;
    const startPoint: Vector3 = new Vector3(startX, insertHelper.startPoint.y + 0.01, positionZ);
    const endPoint: Vector3 = new Vector3(endX, insertHelper.endPoint.y + 0.01, positionZ);

    const descriptors: DimensionDescriptor[] = this.collectDimensionDescriptorsForLockingClips(startPoint, endPoint, lockingClipConnectionContainers);
    descriptors.forEach((descriptor, index) => {
      if (index >= lockingClipConnectionContainers.length) {
        return;
      }
      this.createPointer(dimensionElements, descriptor.endPoint, index);
    });
    let maxSizeLevel = this.processDimensionDescriptors(descriptors, dimensionElements);

    const commonDimension: DimensionElement = this.createCommonDimensionElement(startPoint, endPoint, maxSizeLevel);
    dimensionElements.push(commonDimension);

    const endDescriptors: DimensionDescriptor[] = this.collectStartEndDimensionDescriptors(
      startPoint,
      endPoint,
      lockingClipConnectionContainers,
      this.updateMinLevelValueByDescriptors(descriptors)
    );
    const endDimensions: DrillingHoleDimensionElement[] = this.createStartEndDimensionElements(endDescriptors);
    dimensionElements.push(...endDimensions);

    dimensionElements.push(this.createZeroLabel(startPoint, maxSizeLevel));
    this.processConnectedContainers(lockingClipConnectionContainers, dimensionElements);
  }

  private createPointer(dimensionsElements: Object3D[], endPoint: Vector3, index: number): void {
    const circle: Mesh = new Mesh(new SphereGeometry(DrillingHoleDimensionElement.POINT_RADIUS, 32, 32));
    const circlePosition = endPoint.clone().add(new Vector3(0, 0, -DrillingHoleDimensionElement.POINT_RADIUS / 2));
    circle.position.set(circlePosition.x, circlePosition.y, circlePosition.z);
    circle.material = new MeshBasicMaterial({ color: 0x0fab00 });
    dimensionsElements.push(circle);

    const text: SimpleTextValueVO = new SimpleTextValueVO();
    text.text = index.toString();
    const label: LabelElement = new LabelElement(text, 10);
    const labelPosition = new Vector3(circle.position.x, circle.position.y, circle.position.z);
    label.position.set(labelPosition.x, labelPosition.y, labelPosition.z);
    dimensionsElements.push(label);
  }

  private getLockingClipConnections(): Connection[] {
    let lockingClipConnections: Connection[] = [];

    for(const articleUsage of ProjectUtils.getFirstArticleUsages(this.projectModel.currentProject)) {
      let article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(articleUsage);
      if (ArticleUtils.isLockingClipMountingType(article)) {
        let connections: Connection[] = this.containerService.findArticleContainerInGlobal(articleUsage.id)?.connections ?? [];
        for (let j = 0; j < connections.length; j++) {
          if (ConnectionInfoUtils.isGivingConnection(article, connections[j].connectionName)) {
            lockingClipConnections.push(connections[j]);
          }
        }
      }
    }
    return lockingClipConnections;
  }

  private createCommonDimensionElement(startPoint: Vector3, endPoint: Vector3, level: number): DimensionElement {
    return new DimensionElement(
      this.sceneManager,
      [startPoint, startPoint.clone().add(new Vector3(0, 0, -525)), endPoint.clone().add(new Vector3(0, 0, -525)), endPoint],
      this.createTextValue(this.formatDimension(startPoint.distanceTo(endPoint) / 10)),
      this.material,
      level + 1
    );
  }

  private createRailDimensionLineElement(dimensionElements: ILayerElement[]): void {
    const railContainer: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();
    const railWidth: number = Math.abs(railContainer.bounds.min.z) + railContainer.bounds.max.z;
    const railWidthInMM: number = UnitConverter.fromToRounded('3d', 'mm', railWidth, 1);
    const widthString: string = this.localeResourceManager.formatNumber(railWidthInMM) + ' mm';
    const startRail: Vector3 = new Vector3(this.railLineDistanceToRail, 0, railContainer.bounds.max.z);
    const endRail: Vector3 = new Vector3(this.railLineDistanceToRail, 0, railContainer.bounds.min.z);
    dimensionElements.push(this.createVerticalLineDimensionLabel(widthString, startRail.x));
    dimensionElements.push(...this.createVerticalArrow(startRail, endRail));
  }


  private createProjectWidthLabel(text: string): LabelElement {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    const labelLength: number = DimensionUtils.getTextWidth(textValue, LabelElement.DEFAULT_FONT_SIZE);
    const widthLabel: LabelElement = new LabelElement(
      textValue,
      LabelElement.DEFAULT_FONT_SIZE,
      undefined,
      labelLength
    );
    widthLabel.name = DimensionLayerElementCreator.WIDTH_LABEL_NAME;
    widthLabel.position.set(this.getDimensionLabelXPosition(), 0, 0);
    return widthLabel;
  }

  private createProjectHeightLabel(text: string): LabelElement {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    const labelLength: number = DimensionUtils.getTextWidth(textValue, LabelElement.DEFAULT_FONT_SIZE);
    const heightLabel: LabelElement = new LabelElement(
      textValue,
      LabelElement.DEFAULT_FONT_SIZE,
      undefined,
      labelLength
    );
    heightLabel.name = DimensionLayerElementCreator.HEIGHT_LABEL_NAME;
    heightLabel.position.set(this.getDimensionLabelXPosition(), 0, -100);
    return heightLabel;
  }

  private getDimensionLabelXPosition(): number {
    let x: number = 0;
    const maxLabelLengthText: string = this.localeResourceManager.getValueByKey('layerManager.dimension.projectLength', [
      this.localeResourceManager.getValueByKey('layerManager.dimension.calculatingFailed'),
    ]);
    const maxLabelLengthTextValue: SimpleTextValueVO = this.createTextValue(maxLabelLengthText);
    const maxLabelLength: number = DimensionUtils.getTextWidth(maxLabelLengthTextValue, LabelElement.DEFAULT_FONT_SIZE);
    const distance: number = 0;
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (IsProjectType.visibleMountingRail(this.projectModel.currentProject)) {
      x = insertHelper.startPoint.x;
    } else {
      const allArticlesIncludedInCalculation: ArticleContainer[] = this.containerService
        .enumerateArticleContainersInGlobal()
        .filter((articleContainer) => this.isIncludedInCalculation(articleContainer));
      if (!ArrayUtils.isEmpty(allArticlesIncludedInCalculation)) {
        const startArticleGlobalPosition: Vector3 = this.calculateMinGlobalPointOfArticles(allArticlesIncludedInCalculation);
        x = startArticleGlobalPosition.x;
      }
    }
    return x + maxLabelLength / 2 + distance;
  }

  private createDimensionDescriptorsForArticles(articles: ArticleContainer[], useMiddleHeightPoint: boolean = false): DimensionDescriptor[] {
    const dimensionDescriptors: DimensionDescriptor[] = [];
    const defaultFontScale: number = ConfiguratorParameters.labeling.defaultFontScale;

    for (const container of articles) {
      const maxY: number = this.calculateAccessoryHighestPoint(container) + DimensionLayerElementCreator.BUSBAR_OR_COVER_Y_OFFSET;

      const maxPoint: Vector3 = this.calculateMaxGlobalArticlePoint(container);
      const minPoint: Vector3 = this.calculateMinGlobalArticlePoint(container);

      const zPoint: number = useMiddleHeightPoint ? (minPoint.z + maxPoint.z - this.DIMENSION_HEIGHT) / 2 : minPoint.z;
      const startPoint: Vector3 = new Vector3(minPoint.x, maxY, zPoint);
      const endPoint: Vector3 = new Vector3(maxPoint.x, maxY, zPoint);

      const fontScale: number =
        defaultFontScale * (1 - DimensionLayerElementCreator.FONT_SCALE_COEF * (maxY / DimensionLayerElementCreator.FONT_SCALE_BASE_DISTANCE));

      dimensionDescriptors.push(
        this.createDimensionDescriptor(startPoint, endPoint, false, undefined, DimensionElement.DEFAULT_FONT_SIZE, fontScale, 0.5)
      );
    }

    return dimensionDescriptors;
  }

  private calculateAccessoryHighestPoint(container: ArticleContainer): number {
    let result: number = 0;
    const parentContainerGUID: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(container.articleUsage);
    const parentContainer: ArticleContainer | undefined = this.containerService.findArticleContainerInGlobal(parentContainerGUID);
    if(!Log.assertExists(parentContainer)) {
      return 0;
    }
    const containersToCheck: ArticleContainer[] = this.getConnectedContainers(parentContainer);
    containersToCheck.push(parentContainer);

    for (const articleContainer of containersToCheck) {
      if (ArticleUtils.isWireBridgeArticle(articleContainer.article)) {
        continue;
      }
      const maxPoint: Vector3 = this.calculateMaxGlobalArticlePoint(articleContainer);
      result = Math.max(result, maxPoint.y);
    }
    return Math.floor(result);
  }

  private getConnectedContainers(container: ArticleContainer): ArticleContainer[] {
    const connectedContainers: ArticleContainer[] = [];
    const connectedUsageIds: string[] = ArticleUsageUtils.getAcceptingAreaIds(container.articleUsage);

    for (const guid of connectedUsageIds) {
      const connectedContainer: ArticleContainer | undefined = this.containerService.findArticleContainerInGlobal(guid);
      if(Log.assertExists(connectedContainer)) {
        connectedContainers.push(connectedContainer);
        ArrayUtils.mergeArrays(connectedContainers, this.getConnectedContainers(connectedContainer));
      }
    }
    return connectedContainers;
  }

  private collectDimensionDescriptors(
    startPoint: Vector3,
    endPoint: Vector3,
    connectedArticles: ArticleContainer[],
    leadingArticlesPoints: number[]
  ): DimensionDescriptor[] {
    let {isLeadingStart, isLeadingEnd} = this.getDimensionsLeadingInfo();

    let sizeStartPoint: Vector3 = startPoint.clone();
    let sizeEndPoint: Vector3 = startPoint.clone();
    let hasLeadingValue: boolean = false;

    const dimensionDescriptors: DimensionDescriptor[] = [];
    for (const article of connectedArticles) {
      const articleGlobalStartPosition: Vector3 = this.calculateMinGlobalArticlePoint(article);
      const articleGlobalEndPosition: Vector3 = this.calculateMaxGlobalArticlePoint(article);
      // change Z so that the points are at the dimension level
      const articleStartPosition: Vector3 = new Vector3(articleGlobalStartPosition.x, startPoint.y, startPoint.z);
      const articleEndPosition: Vector3 = new Vector3(articleGlobalEndPosition.x, startPoint.y, startPoint.z);
      hasLeadingValue = false;

      // article stands at a distance from previous article
      // multiply x-positions by -1 because in html mounting rail aligned by -x axis
      const isFreeSpaceBefore: boolean = -articleStartPosition.x - -sizeEndPoint.x >= this.DIMENSION_EPSILON;
      const isCustomArticle: boolean = ArticleUtils.isCustomArticle(article.article);

      // size for the previous group of neighbor articles
      if ((isFreeSpaceBefore || isCustomArticle) && sizeStartPoint.distanceTo(sizeEndPoint) > 0) {
        hasLeadingValue = this.hasLeadingValueBetweenPoints(sizeStartPoint, sizeEndPoint, leadingArticlesPoints);
        dimensionDescriptors.push(this.createDimensionDescriptor(sizeStartPoint, sizeEndPoint, hasLeadingValue));
      }

      // size for free space before articles
      hasLeadingValue = this.hasLeadingValueBetweenPoints(sizeEndPoint, articleStartPosition, leadingArticlesPoints);
      if ((isFreeSpaceBefore && hasLeadingValue) || (isLeadingStart && sizeEndPoint.equals(startPoint))) {
        // if this is an offset from the start of mounting rail and the start size is leading then this size should be bold
        dimensionDescriptors.push(this.createDimensionDescriptor(sizeEndPoint, articleStartPosition, true));
        sizeStartPoint = articleStartPosition;
      } else if (isFreeSpaceBefore) {
        dimensionDescriptors.push(this.createDimensionDescriptor(sizeEndPoint, articleStartPosition, false));
        sizeStartPoint = articleStartPosition;
      }

      // size for custom article
      if (isCustomArticle) {
        dimensionDescriptors.push(this.createDimensionDescriptor(articleStartPosition, articleEndPosition, false));
        sizeStartPoint = articleEndPosition;
      }

      sizeEndPoint = articleEndPosition;
    }

    // size for the last group of neighbor articles
    if (sizeStartPoint.distanceTo(sizeEndPoint) > 0) {
      dimensionDescriptors.push(this.createDimensionDescriptor(sizeStartPoint, sizeEndPoint, false));
    }

    // size for free space between articles and the end of mounting rail, it should be bold if the start size is NOT leading
    if (endPoint.distanceTo(sizeEndPoint) >= this.DIMENSION_EPSILON || isLeadingEnd) {
      hasLeadingValue = this.hasLeadingValueBetweenPoints(sizeEndPoint, endPoint, leadingArticlesPoints);
      dimensionDescriptors.push(this.createDimensionDescriptor(sizeEndPoint, endPoint, hasLeadingValue || isLeadingEnd));
    }

    return dimensionDescriptors;
  }

  private getDimensionsLeadingInfo(): {isLeadingStart: boolean, isLeadingEnd: boolean} {
    let isLeadingStart: boolean = false;
    let isLeadingEnd: boolean = false;
    if (this.isMountingRailLeadingRequired()) {
      const leadingValue: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
        this.mountingRailHelper.getMountingRailUsage(),
        ArticleUsageInfoPropertyDef.LEADING_MEASUREMENT
      ) as MountingRailCarrierType;
      if (leadingValue) {
        isLeadingStart = leadingValue === MountingRailCreationModel.LEADING_START || leadingValue === MountingRailCreationModel.LEADING_BOTH;
        isLeadingEnd = leadingValue === MountingRailCreationModel.LEADING_END || leadingValue === MountingRailCreationModel.LEADING_BOTH;
      } else {
        // default value
        isLeadingStart = true;
      }
    }
    return {isLeadingStart, isLeadingEnd};
  }

  private isMountingRailLeadingRequired(): boolean {
    return IsProjectType.visibleMountingRail(this.projectModel.currentProject);
  }

  private hasLeadingValueBetweenPoints(start: Vector3, end: Vector3, leadingArticlesPoints: number[]): boolean {
    for (const value of leadingArticlesPoints) {
      if ((start.x < value && end.x > value) || (start.x > value && end.x < value)) {
        return true;
      }
    }
    return false;
  }

  private collectLeadingArticlesPoints(connectedArticles: ArticleContainer[], insertHelper: InsertArticleHelper): number[] {
    const leadingArticlesPoints: number[] = [];
    for (const article of connectedArticles) {
      if (ArticleUtils.isBlockingZone(article.article) || ArticleUtils.isEmptyZoneArticle(article.article)) {
        const isLeadingValueProperty: boolean = ArticleUsageUtils.getArticleUsageInfoPropertyBooleanValue(
          article.articleUsage,
          ArticleUsageInfoPropertyDef.IS_LEADING_VALUE
        );
        if (isLeadingValueProperty) {
          // collected x dimensions of center of blocking/empty zones
          const line: Line = insertHelper.getArticleLine(article);
          leadingArticlesPoints.push((line.start.x + line.end.x) / 2);
        }
      }
    }
    return leadingArticlesPoints;
  }

  public getProjectWidth(): number {
    let pWidth: number = 0;
    if (!this.projectModel || !this.mountingRailHelper) {
      return pWidth;
    }

    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const maxZ: number = this.findMaxZValue();
    const connectedArticles: ArticleContainer[] =
      insertHelper.getAllConnectedArticles()
        .filter((articleContainer) => this.isIncludedInCalculation(articleContainer));

    let startPoint: Vector3;
    let endPoint: Vector3;
    if (IsProjectType.visibleMountingRail(this.projectModel.currentProject)) {
      startPoint = new Vector3(insertHelper.startPoint.x, insertHelper.startPoint.y, maxZ);
      endPoint = new Vector3(insertHelper.endPoint.x, insertHelper.endPoint.y, maxZ);
    } else {
      const startArticleGlobalPosition: Vector3 = this.calculateMinGlobalArticlePoint(connectedArticles[0]);
      const endArticleGlobalPosition: Vector3 = this.calculateMaxGlobalArticlePoint(connectedArticles[connectedArticles.length - 1]);
      startPoint = new Vector3(startArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
      endPoint = new Vector3(endArticleGlobalPosition.x, startArticleGlobalPosition.y, maxZ);
    }
    pWidth = startPoint.distanceTo(endPoint) / 10;
    return pWidth;
  }

  public collectDimensionDescriptorsForLockingClips(startPoint: Vector3, endPoint: Vector3, articles: ArticleContainer[]): DimensionDescriptor[] {
    if (ArrayUtils.isEmpty(articles)) {
      return [];
    }

    const descriptors: DimensionDescriptor[] = [];
    let offset: number = 0;
    const originalStartPoint: Vector3 = startPoint.clone();
    let sizeEndPoint: Vector3 = startPoint.clone();
    const mountingRailVector: Vector3 = endPoint.clone().sub(startPoint);
    mountingRailVector.normalize();

    let index: number = 1;
    let descriptor: DimensionDescriptor;
    for (const article of articles) {
      let articleOffset: number = this.calculateOffset(article);
      const articleWidth: number = this.calculateWidth(article);
      let pointerOnRight: boolean = true;

      if (articleOffset === 0 || Math.abs(articleOffset) < 0.001) {
        offset = 0;
        articleOffset = Math.abs(articleWidth / 2);
        pointerOnRight = false;
      } else if (articleOffset > startPoint.x) {
        articleOffset = Math.abs(startPoint.x + (articleOffset - articleWidth / 2));
        offset = Math.abs(startPoint.x);
      } else if (articleOffset < endPoint.x) {
        articleOffset = articleOffset + articleWidth / 2;
      }

      const articleStartPosition: Vector3 = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(offset)));
      const articleEndPosition: Vector3 = startPoint.clone().add(this.multiplyVector(mountingRailVector, Math.abs(articleOffset)));
      offset = articleOffset;

      descriptor = this.createDimensionDescriptor(
        articleStartPosition,
        articleEndPosition,
        false,
        this.createTextValue(this.formatDimension(originalStartPoint.distanceTo(articleEndPosition) / 10))
      );
      descriptor.index = index;
      descriptor.pointerOnRight = pointerOnRight;
      descriptors.push(descriptor);

      sizeEndPoint = articleEndPosition;
      index++;
    }

    if (endPoint.distanceTo(sizeEndPoint) >= 0.85) {
      descriptor = this.createDimensionDescriptor(sizeEndPoint, endPoint, false, this.createTextValue(''));
      descriptor.pointerRequired = false;
      descriptor.isOuterLabel = false;
      descriptors[descriptors.length] = descriptor;
    }

    return descriptors;
  }

  private createZeroLabel(startPoint: Vector3, level: number): LabelElement {
    const p1: Vector3 = startPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT));
    const position: Vector3 = MathUtils.getInterpolated(p1, startPoint, DimensionElement.ARROW_MULTIPLIER);
    const text: TextValueVO = this.createTextValue('0');
    position.x = position.x + DimensionUtils.getTextWidth(text as SimpleTextValueVO) / 2;
    position.z += -((level + 1) * DimensionElement.LEVEL_OFFSET) + DimensionUtils.getTextHeight(text as SimpleTextValueVO) / 2;

    const label: LabelElement = new LabelElement(text);
    label.position.set(position.x, position.y, position.z);

    return label;
  }

  private createStartEndDimensionElements(endDescriptors: DimensionDescriptor[]): DrillingHoleDimensionElement[] {
    const endDimensionElements: DrillingHoleDimensionElement[] = [];
    for (const dimensionDescriptor of endDescriptors) {
      const dimensionStartPoint: Vector3 = dimensionDescriptor.startPoint.clone();
      const dimension: DrillingHoleDimensionElement = new DrillingHoleDimensionElement(
        this.sceneManager,
        [
          dimensionStartPoint,
          dimensionStartPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
          dimensionDescriptor.endPoint.clone().add(new Vector3(0, 0, this.DIMENSION_HEIGHT)),
          dimensionDescriptor.endPoint,
        ],
        dimensionDescriptor.text,
        this.material,
        dimensionDescriptor.sizeLevel,
        325 - 325 / 2.5,
        undefined,
        DimensionElement.LEVEL_OFFSET,
        dimensionDescriptor.fontSize,
        dimensionDescriptor.fontScale,
        dimensionDescriptor.arrowMultiplier,
        false,
        false
      );

      endDimensionElements.push(dimension);
    }

    return endDimensionElements;
  }

  private processConnectedContainers(drillingHoleContainers: ArticleContainer[], elements: ILayerElement[]): void {
    const insertHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    const labelsMap: Map<ArticleContainer, string[]> = this.collectLabels(drillingHoleContainers);

    labelsMap.forEach((labels: string[], articleContainer: ArticleContainer) => {
      const pointerPosition: Vector3 = insertHelper.startPoint.clone();
      pointerPosition.y += 5;
      pointerPosition.x = this.calculateOffset(articleContainer);

      const initLabelPosition: Vector3 = pointerPosition.clone();
      initLabelPosition.y += 5;
      initLabelPosition.z -= 325 + 325 / 2;

      labels.forEach((label: string, index: number) => {
        const labelPosition: Vector3 = initLabelPosition.clone();
        labelPosition.x -= LabelElement.DEFAULT_LABEL_WIDTH * index;
        const textValue: SimpleTextValueVO = this.createArticleNumberTextValue(label);
        const element: PointerLabelElement = new PointerLabelElement(
          labelPosition,
          pointerPosition,
          textValue,
          this.longestLabelLength,
          index === 0
        );
        elements.push(element);
      });
    });
  }

  private createArticleNumberTextValue(text: string): SimpleTextValueVO {
    const textValue: SimpleTextValueVO = this.createTextValue(text);
    textValue.hAlignment = -1;
    return textValue;
  }

  private getFormattedDimensionString(value: number) {
    if (!NumberUtils.isNumber(value)) {
      return '';
    }
    return this.localeResourceManager.formatNumber(MathUtils.roundNumberValue(value, 1), true);
  }

  private getBusbarOrCoverArticles() {

    const articleContainers =
      this.containerService.enumerateArticleContainersInGlobal()
        .filter((articleContainer) => this.isIncludedInCalculation(articleContainer));

    const busbarOrCoverArticles = [];

    for (const articleContainer of articleContainers) {
      if (ArticleUtils.isBusbarArticle(articleContainer.article)) {
        const behaviorProperties: PropertyVO[] = articleContainer.article.behaviorProperties;
        const isBusbarWithoutDimension: boolean = PropertyUtils.getBooleanPropertyValue(
          behaviorProperties,
          ArticleBehaviorPropertyDef.IS_BUSBAR_WITHOUT_DIMENSION
        );
        if (!isBusbarWithoutDimension) {
          busbarOrCoverArticles.push(articleContainer);
        }
      } else if (ArticleUtils.isCoverArticle(articleContainer.article)) {
        busbarOrCoverArticles.push(articleContainer);
      }
    }

    return busbarOrCoverArticles;
  }
}
